/**
 * Interactive Site Plan Overlay Plugin for Search Results Map
 *
 * Manages the lifecycle of SVG layer overlays for site plans where
 * IsInteractiveSitePlan = true.
 * Renders zoom-dependent SVGZ layers.
 *
 * Separation from sitePlan.plugin:
 * - Different rendering pipeline: stacked SVG layers vs a single static image
 * - Different data requirements: SvgLayers + SitePlanLots vs just MapImageUrl
 * - Async SVGZ loading and zoom-based layer management is a distinct lifecycle concern
 *
 * Design:
 * - Renderer: manages SvgLayerOverlay handles keyed by SitePlanId via facade
 * - Layer visibility: driven by LAYER_ZOOM_MAP thresholds, updated incrementally on zoom
 * - Three store subscriptions: sitePlans, interactiveSitePlans, and mapResults,
 *   all scheduleRender on change
 * - rAF debounce: coalesces rapid updates from zoom, bounds, and data changes
 *
 * @module searchResultsMap/plugins/interactiveSitePlan.plugin
 */

import {
  map,
  distinctUntilChanged,
} from 'rxjs';
import {
  selectSitePlansById,
  selectInteractiveSitePlansById,
  selectMapResultCommunityIds,
} from '../redux/selectors';
import fetchInteractiveSitePlansEpic from '../redux/fetchInteractiveSitePlansEpic';
import cleanupSitePlansEpic from '../redux/cleanupSitePlansEpic';
import {
  isPolygonInBounds,
  hasCameraChanged,
} from '../services/geometry';

import buildLotRenderer from '../services/lotRenderer';
import { SITE_PLAN_CLASS_NAMES, LOT_CLASS_NAMES } from '../services/sitePlanBehavior';
import { SITE_PLAN_RENDER_ZOOM_THRESHOLD } from '../constants';

/**
 * Maps each SVG layer type to the minimum zoom level at which it should be visible.
 * Matches the product-defined zoomLevelLayersLookup thresholds.
 * Layer types not listed default to SITE_PLAN_RENDER_ZOOM_THRESHOLD
 * (visible whenever site plans are active).
 */
const LAYER_ZOOM_MAP = new Map([
  ['StreetNames', 17],
  ['Walkway', 16],
  ['Water', 15],
  ['Golf', 15],
  ['Grass', 14],
  ['Ground', 14],
  ['Street', 14],
  ['Curbs', 14],
  ['Main', 14],
  ['MainLots', 0],
  ['LotPolygons', 0],
]);

/**
 * Returns the SvgLayerType strings that should be visible at the given zoom level.
 * A layer is visible when zoom >= its minimum threshold from LAYER_ZOOM_MAP.
 * Types not in the map use SITE_PLAN_RENDER_ZOOM_THRESHOLD as their threshold.
 */
function getVisibleLayerTypes(svgLayers, zoom) {
  return svgLayers
    .filter((layer) => {
      const minZoom = LAYER_ZOOM_MAP.has(layer.svgLayerType)
        ? LAYER_ZOOM_MAP.get(layer.svgLayerType)
        : SITE_PLAN_RENDER_ZOOM_THRESHOLD;

      return zoom >= minZoom;
    })
    .map((layer) => layer.svgLayerType);
}

/**
 * Creates a renderer that manages SvgLayerOverlay handles
 * for interactive site plans.
 * All Google Maps interactions are delegated to the facade.
 */
function createRenderer(mapFacade, eventHandlers) {
  const overlays = new Map();

  /**
   * Attaches a new SVG layer overlay to the map.
   * Only creates the overlay when both site plan
   * and interactive data are available.
   *
   * @param {Object} sitePlan
   * @param {Object|null} interactiveData
   * @param {number} zoom
   */
  function attachOverlay(sitePlan, interactiveData, zoom) {
    const overlay = mapFacade.createSvgLayerOverlay(
      sitePlan,
      interactiveData,
      eventHandlers,
    );

    if (!overlay) {
      return;
    }

    overlay.attach();

    // setVisibleLayerTypes must run after onAdd completes, because onAdd
    // builds layerEntries. Calling it before onAdd fires is a no-op.
    overlay.onReady(() => {
      overlay.setVisibleLayerTypes(
        getVisibleLayerTypes(sitePlan.svgLayers, zoom),
      );
    });

    overlays.set(String(sitePlan.sitePlanId), overlay);
  }

  /**
   * Detaches a single overlay by SitePlanId.
   *
   * @param {string} sitePlanId
   */
  function detachOverlay(sitePlanId) {
    const overlay = overlays.get(sitePlanId);

    if (!overlay) {
      return;
    }

    overlay.detach();
    overlays.delete(sitePlanId);
  }

  /**
   * Removes all overlays from the map.
   */
  function detachAllOverlays() {
    overlays.forEach((overlay) => overlay.detach());
    overlays.clear();
  }

  /**
   * Incrementally updates visible overlays against
   * the current zoom and viewport bounds.
   *
   * On each call:
   * - Overlays that leave the viewport or exceed max-zoom are detached
   * - New in-viewport overlays are attached
   * - Existing overlays are synced and their layer visibility is updated
   *
   * @param {Object} sitePlans
   * @param {Object} interactiveSitePlans
   * @param {Set<number>} resultCommunityIds
   * @param {number} zoom
   * @param {{
   *   south: number,
   *   north: number,
   *   west: number,
   *   east: number
   * }|null} bounds
   */
  function update(
    sitePlans,
    sitePlanValues,
    interactiveSitePlans,
    resultCommunityIds,
    zoom,
    bounds,
  ) {
    if (zoom < SITE_PLAN_RENDER_ZOOM_THRESHOLD) {
      detachAllOverlays();
      return;
    }

    if (resultCommunityIds.size === 0) {
      detachAllOverlays();
      return;
    }

    // Single-pass iteration avoids the intermediate arrays produced by .filter().map().
    // Filter order: cheap O(1) Set check first to skip the string-converting
    // object lookup for communities not present in current results.
    const nextVisibleIds = new Set();

    sitePlanValues.forEach((sitePlan) => {
      if (
        sitePlan.isInteractiveSitePlan &&
        resultCommunityIds.has(sitePlan.communityId) &&
        interactiveSitePlans[String(sitePlan.sitePlanId)] &&
        isPolygonInBounds(sitePlan, bounds)
      ) {
        nextVisibleIds.add(String(sitePlan.sitePlanId));
      }
    });

    // Collect keys to detach without spread syntax to avoid an extra array allocation.
    const idsToDetach = [];

    overlays.forEach((_, id) => {
      if (!nextVisibleIds.has(id)) {
        idsToDetach.push(id);
      }
    });

    idsToDetach.forEach(detachOverlay);
    nextVisibleIds.forEach((sitePlanId) => {
      const sitePlan = sitePlans[sitePlanId];
      const interactiveData = interactiveSitePlans[sitePlanId];

      if (!sitePlan || !interactiveData) {
        return;
      }

      if (overlays.has(sitePlanId)) {
        const overlay = overlays.get(sitePlanId);

        overlay.syncData(sitePlan, interactiveData);

        overlay.setVisibleLayerTypes(
          getVisibleLayerTypes(sitePlan.svgLayers, zoom),
        );

        return;
      }

      attachOverlay(sitePlan, interactiveData, zoom);
    });
  }

  return {
    update,
    detachAllOverlays,
  };
}

/**
 * Creates the Interactive Site Plan plugin for the plugin host.
 *
 * Plugin lifecycle:
 * - init: Initialises shared state
 * - start: Subscribes to store (sitePlans + interactiveSitePlans),
 *   listens to map events
 * - stop: Tears down subscriptions, map event listeners, and all overlays
 *
 * @param {Object} [options]
 * @param {Function} [options.onSitePlanClick]
 * @param {Function} [options.onSitePlanEnter]
 * @param {Function} [options.onSitePlanLeave]
 *
 * @returns {Object}
 */
export default function createInteractiveSitePlanPlugin({
  onSitePlanClick,
  onSitePlanEnter,
  onSitePlanLeave,
} = {}) {
  const state = {
    started: false,
    renderer: null,
    subscriptions: [],
    mapEventUnsubscribers: [],
    sitePlans: {},
    sitePlanValues: [],
    interactiveSitePlans: {},
    resultCommunityIds: new Set(),
    store$: null,
    mapFacade: null,
    requestFrameId: null,
    lastRenderedSnapshot: null,
  };

  /**
   * Schedules a debounced render via rAF.
   * Coalesces rapid updates from data, zoom,
   * and bounds changes into one render cycle.
   *
   * Zoom and bounds are read fresh from getCameraSnapshot()
   * at render time so that store-triggered renders
   * never depend on stale cached camera state.
   */
  function scheduleRender() {
    if (state.requestFrameId !== null) {
      return;
    }

    state.requestFrameId = requestAnimationFrame(() => {
      state.requestFrameId = null;

      const snapshot = state.mapFacade?.getCameraSnapshot();
      state.lastRenderedSnapshot = snapshot ?? null;

      state.renderer?.update(
        state.sitePlans,
        state.sitePlanValues,
        state.interactiveSitePlans,
        state.resultCommunityIds,
        snapshot?.zoom ?? 0,
        snapshot?.bounds ?? null,
      );
    });
  }

  /**
   * Schedules a render only when the camera has moved since the last render.
   * Used exclusively by the idle handler to avoid redundant update() calls
   * when idle fires without an actual viewport change.
   */
  function scheduleRenderOnCameraChange() {
    const snapshot = state.mapFacade?.getCameraSnapshot();
    if (!hasCameraChanged(state.lastRenderedSnapshot, snapshot ?? null)) {
      return;
    }
    scheduleRender();
  }

  return {
    name: 'interactive-site-plan',

    async init(context) {
      context.services.epic$.next(fetchInteractiveSitePlansEpic);
      context.services.epic$.next(cleanupSitePlansEpic);

      context.state.interactiveSitePlan = {
        started: false,
        ready: false,
      };
    },

    async start(context) {
      if (state.started) {
        return;
      }

      const mapFacade = context.services.mapFacade;

      if (!mapFacade) {
        return;
      }

      state.store$ = context.services.store$;
      state.mapFacade = mapFacade;

      const eventHandlers = {
        classNames: SITE_PLAN_CLASS_NAMES,
        lotRenderer: buildLotRenderer(LOT_CLASS_NAMES),
        onSitePlanClick: onSitePlanClick ?? null,
        onSitePlanEnter: onSitePlanEnter ?? null,
        onSitePlanLeave: onSitePlanLeave ?? null,
      };

      state.renderer = createRenderer(
        mapFacade,
        eventHandlers,
      );

      state.subscriptions.push(
        state.store$
          .pipe(
            map(selectSitePlansById),
            distinctUntilChanged(),
          )
          .subscribe((data) => {
            state.sitePlans = data;
            state.sitePlanValues = Object.values(data);
            scheduleRender();
          }),
      );

      state.subscriptions.push(
        state.store$
          .pipe(
            map(selectInteractiveSitePlansById),
            distinctUntilChanged(),
          )
          .subscribe((data) => {
            state.interactiveSitePlans = data;
            scheduleRender();
          }),
      );

      state.subscriptions.push(
        state.store$
          .pipe(
            map(selectMapResultCommunityIds),
            distinctUntilChanged(),
          )
          .subscribe((ids) => {
            state.resultCommunityIds = ids;
            scheduleRender();
          }),
      );

      const unsubscribeBounds = mapFacade.onMapEvent(
        'idle',
        () => {
          scheduleRenderOnCameraChange();
        },
      );

      state.mapEventUnsubscribers.push(unsubscribeBounds);

      state.started = true;

      context.state.interactiveSitePlan = {
        started: true,
        ready: true,
      };
    },

    async stop(context) {
      if (state.requestFrameId !== null) {
        cancelAnimationFrame(state.requestFrameId);
        state.requestFrameId = null;
      }

      state.subscriptions.forEach((subscription) => {
        subscription?.unsubscribe?.();
      });

      state.subscriptions = [];

      state.mapEventUnsubscribers.forEach((unsubscribe) => {
        unsubscribe?.();
      });

      state.mapEventUnsubscribers = [];

      state.renderer?.detachAllOverlays();

      state.renderer = null;
      state.store$ = null;
      state.mapFacade = null;
      state.started = false;
      state.sitePlans = {};
      state.sitePlanValues = [];
      state.interactiveSitePlans = {};
      state.resultCommunityIds = new Set();
      state.lastRenderedSnapshot = null;

      delete context.state.interactiveSitePlan;
    },
  };
}